import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, iif, of, Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import {
  BoundaryStatus,
  getCalcData,
  hasManualTranslations,
  hasTranslations,
  mergeSavedLotToAutoLotView,
  ReactiveLotData,
} from 'src/app/const';
import {
  Lot,
  LotGQL,
  LotSource,
  UserLotGQL,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import { LimitsService } from 'src/app/services/limits.service';
import { SanctionsService } from 'src/app/services/sanctions.service';

import { environment } from '../../../../../environments/environment';
import { LotViewsService } from '../../../../services/lot-views.service';

@Component({
  selector: 'app-arai-kenki-lot',
  templateUrl: './arai-kenki-lot.component.html',
  styleUrls: ['./arai-kenki-lot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AraiKenkiLotComponent implements OnInit {
  data$: ReactiveLotData = of({});
  loading$ = new BehaviorSubject<boolean>(true);
  refetch$ = new Subject<void>();

  activeSlide: number = 1;

  LotSource = LotSource;
  BoundaryStatus = BoundaryStatus;

  statisticsOpen = false;

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private limitsService: LimitsService,
    private lotViewsService: LotViewsService,
    private lotGQL: LotGQL,
    private userLotGQL: UserLotGQL,
    private sanctionsService: SanctionsService,
  ) {}

  ngOnInit(): void {
    this.data$ = combineLatest([
      this.route.params,
      this.refetch$.asObservable().pipe(startWith(true)),
    ]).pipe(
      tap(() => this.loading$.next(true)),
      map(([params]) => ({
        lotId: params.lot,
        lotType: params.type,
        lotSource: LotSource.Manual,
      })),
      switchMap((initData) =>
        combineLatest([
          this.lotGQL.fetch(
            {
              id: initData.lotId,
              subgroup: initData.lotType,
              lotSource: initData.lotSource,
              siteDomain: environment.lotSiteDomain,
            },
            { fetchPolicy: 'network-only' },
          ),
          this.userLotGQL.fetch(
            {
              id: initData.lotId,
              lotSource: initData.lotSource,
              subgroup: initData.lotType,
              siteDomain: environment.lotSiteDomain,
            },
            { fetchPolicy: 'network-only' },
          ),
        ]).pipe(
          map(([lotResult, userResult]) => ({
            initData,
            lotResult,
            userResult,
          })),
        ),
      ),
      tap(({ initData }) =>
        this.lotViewsService.increment(initData.lotId, initData.lotType, initData.lotSource),
      ),
      switchMap((data) =>
        iif(
          () => Boolean(data.lotResult.data.lot) || Boolean(data.userResult.data.lot),
          of(data).pipe(
            map((data) => ({
              ...data,
              lot: mergeSavedLotToAutoLotView(null, data.lotResult.data.lot),
            })),
            map((data) => ({
              ...data.initData,
              lot: data.lot,
              savedLot: data.userResult.data?.lot,
              icons: data.userResult.data?.lot?.icons ?? [],
              currentUser: data.userResult.data?.currentUser,
              currentUserLastBid: data.userResult.data?.lot?.currentUserLastBid,
              currentUserLimitsOverrideAvailable:
                data.userResult.data?.lot?.currentUserLimitsOverrideAvailable,
              bestBid: data.userResult.data?.lot?.bestBid,
              hasBestBid: data.userResult.data?.lot?.hasBestBid,
              currentUserLotMarks: data.userResult.data?.lot?.currentUserLotMarks || [],
              translations: data.userResult.data?.lot?.translations,
              fullVin: Boolean(data.lot.vin),
              limits: this.limitsService.getLimits(data.lot, data.userResult.data?.lot as Lot),
              hasTranslations: hasTranslations(data.userResult.data?.lot?.translations),
              hasManualTranslations: hasManualTranslations(data.userResult.data?.lot?.translations),
              isUsingSavedImages: Boolean(data.userResult.data.lot),
              isSanctioned: this.sanctionsService.isLotSanctioned(
                data.lot,
                data.userResult.data.currentUser,
              ),
              calcData: getCalcData(
                data.lot,
                'auto',
                data.userResult.data?.lot?.currentUserLastBid?.amount,
              ),
            })),
            tap((data) => {
              const { bid, auctionName, company, modelName, frame } = data.lot;
              this.title.setTitle(
                `${bid} - ${auctionName} - ${company} ${modelName} ${frame} | ECar JP`,
              );
            }),
          ),
          of(data.initData),
        ),
      ),
      tap(() => this.loading$.next(false)),
    );
  }

  goToFirstSlide(isUsingSavedImages: boolean): void {
    if (!isUsingSavedImages) {
      this.activeSlide = 1;
    }
  }

  scrollToTranslations(): void {
    document.getElementById('lotTranslations').scrollIntoView({ behavior: 'smooth' });
  }

  scrollToPreviousSales(): void {
    document.getElementById('previousSales').scrollIntoView({ behavior: 'smooth' });
    this.statisticsOpen = true;
  }
}
